import { sortBy } from '@libs/array';
import { Subscription } from '@libs/subscription';

import * as stockfish from '../stockfish';
import { BrowserFeatures } from './features';

const DEBUG = false;
const MAX_THREADS = Math.max(navigator.hardwareConcurrency - 1, 1);
const UPDATE_THROTTLE_MS = 350;
const MAX_WORKER_RETRIES = 2;

export const UCI_PROVIDERS = {
  sf_17_mt: {
    name: 'Stockfish 17 Lite MT',
    url: stockfish.urlWorkerMT,
    test: [
      'hasWorker',
      'hasWebAssembly',
      'hasSimd',
      'hasSharedMemory',
      // HACK: fix mobile safari shared memory allocation bug
      'canUseLotsOfSharedMemory',
    ],
    threads: MAX_THREADS,
    defaultSearchTimeMs: 10 * 1000,
    supportsElo: true,
  },
  sf_17_st: {
    name: 'Stockfish 17 Lite ST',
    url: stockfish.urlWorkerST,
    test: ['hasWorker', 'hasWebAssembly', 'hasSimd'],
    threads: 1,
    defaultSearchTimeMs: 10 * 1000,
    supportsElo: true,
  },
  sf_10_st: {
    name: 'Stockfish 10 Wasm',
    url: stockfish.urlWorker10,
    test: ['hasWorker', 'hasWebAssembly'],
    threads: 1,
    defaultSearchTimeMs: 10 * 1000,
    supportsElo: false,
  },
  sf_10_st_nowasm: {
    name: 'Stockfish 10',
    url: stockfish.urlWorker10Legacy,
    test: ['hasWorker'],
    threads: 1,
    defaultSearchTimeMs: 10 * 1000,
    supportsElo: false,
  },
} satisfies Record<string, IUciEngineSpecs>;

interface IUciEngineSpecs {
  name: string;
  url: string;
  test: Array<keyof BrowserFeatures>;
  threads: number;
  defaultSearchTimeMs: number;
  supportsElo: boolean;
}

export interface IUciEngineOptions {
  elo?: number;
  skill?: number;
}

export interface IValuationRequest {
  fen: string;
  multiPV?: number;
  depth?: number;
  movetime?: number;
}

export interface IValuationLine {
  fen: string;
  multiPV: number;
  type: 'cp' | 'mate';
  valuation: number;
  depth: number;
  moves: string[];
  source: string;
}

export type UciEngineEvents =
  | { type: 'initialized' }
  | { type: 'progress'; valuations: IValuationLine[]; req: IValuationRequest }
  | {
      type: 'finished';
      valuations: IValuationLine[];
      req: IValuationRequest;
      bestmove: string;
    }
  | {
      type: 'interrupted';
      valuations: IValuationLine[];
      req: IValuationRequest;
      bestmove: string;
    }
  | {
      type: 'error';
      req?: IValuationRequest;
    };

export type UciEngineStatus =
  | 'initializing'
  | 'idle'
  | 'searching'
  | 'stopping'
  | 'interrupted'
  | 'terminated';

export class UciEngine {
  options: IUciEngineOptions;
  specs: IUciEngineSpecs;
  #worker: Worker;
  #req?: IValuationRequest & { color: 'white' | 'black' };
  #reqNext?: IValuationRequest;
  #valuations: Record<number, IValuationLine> = {};
  #subscribtion = new Subscription<UciEngineEvents>();
  #status: UciEngineStatus = 'initializing';
  #publishedTime = 0;
  #workerRetries = 0;
  #cache: Record<string, { req: IValuationRequest; data: IValuationLine[]; bestmove: string }> = {};

  private constructor(specs: IUciEngineSpecs, options: IUciEngineOptions = { skill: 20 }) {
    this.specs = specs;
    this.options = options;
    this.#worker = new Worker(this.specs.url);
    this.#initWorker(this.#worker);
  }

  // --- Statuses

  get status(): UciEngineStatus {
    return this.#status;
  }

  get isInitializing(): boolean {
    return this.#status === 'initializing';
  }

  get isIdle(): boolean {
    return this.#status === 'idle';
  }

  get isSearching(): boolean {
    return this.#status === 'searching';
  }

  get isStopping(): boolean {
    return this.#status === 'stopping';
  }

  get isInterrupted(): boolean {
    return this.status === 'interrupted';
  }

  get isTerminated(): boolean {
    return this.#status === 'terminated';
  }

  // --- Current State

  get valuations() {
    return this.#valuations;
  }

  // --- Public methods

  subscribe(cb: (v: UciEngineEvents) => void) {
    return this.#subscribtion.subscribe(cb);
  }

  evaluate(req: IValuationRequest) {
    // Останавливаем текущий поиск, сохранаяем задачу и ждём когда двиок
    // освободится (в #onMessage)
    if (this.isInitializing || this.isSearching || this.isStopping) {
      this.stop();
      this.#reqNext = req;
      return;
    }

    this.#workerRetries = 0;

    this.#status = 'searching';
    this.#req = { ...req, color: req.fen.match(' b ') ? 'black' : 'white' };
    this.#reqNext = undefined;

    const { fen, multiPV = 1, depth, movetime = this.specs.defaultSearchTimeMs } = req;

    this.#valuations = {};

    // попробуем достать из кэша
    const cached = this.#getCached(req);
    if (cached) {
      this.#subscribtion.publish({
        type: 'finished',
        valuations: cached.data,
        req: this.#req!,
        bestmove: cached.bestmove,
      });

      this.#status = 'idle';
      return;
    }

    for (let i = 1; i <= multiPV; i++) {
      this.#valuations[i] = {
        fen,
        multiPV: i,
        depth: 0,
        moves: [],
        valuation: 0,
        type: 'cp',
        source: '',
      };
    }
    this.#publish(true);

    this.#uci('setoption name Move Overhead value 0'); // не добавлять задержку
    this.#uci('setoption name Hash value 32'); // добавим памяти (16 -> 32)
    this.#uci(`setoption name MultiPV value ${multiPV}`);
    this.#uci(`position fen ${fen}`);

    const go = ['go'];
    if (depth) go.push(`depth ${depth}`);
    go.push(`movetime ${movetime}`);
    this.#uci(go.join(' '));
  }

  stop() {
    if (this.isIdle || this.isInterrupted) return;

    if (this.isSearching) {
      this.#uci('stop');
      this.#status = 'stopping';
    }

    this.#reqNext = undefined;
  }

  terminate() {
    this.#status = 'terminated';
    this.#uci('quit');
    this.#reqNext = undefined;
    this.#subscribtion.clear();
    this.#worker.terminate();
  }

  // --- Ststic methods

  static create(specs: IUciEngineSpecs, options?: IUciEngineOptions): UciEngine {
    return new UciEngine(specs, options);
  }

  static getFirstSupported(): (typeof UCI_PROVIDERS)[keyof typeof UCI_PROVIDERS] {
    return Object.values(UCI_PROVIDERS).find((p) => this.isSupported(p))!;
  }

  static createDefault(options?: IUciEngineOptions): UciEngine {
    return this.create(this.getFirstSupported(), options);
  }

  static createLite(skill?: number): UciEngine {
    const firstSupported = [UCI_PROVIDERS.sf_10_st, UCI_PROVIDERS.sf_10_st_nowasm].find((p) =>
      this.isSupported(p),
    )!;
    return this.create(firstSupported, { skill: skill ?? 20 });
  }

  static isSupported(specs: IUciEngineSpecs) {
    return specs.test.every((t) => BrowserFeatures.instance[t]);
  }

  // --- Helpers

  /**
   * Отправить комманду движку
   */
  #uci(cmd: string) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[send]', cmd);
    }

    this.#worker.postMessage(cmd);
  }

  /**
   * Подготовить и опубликовать текущее состояние оценки
   */
  #publish(force?: boolean) {
    const now = Date.now();

    // throttle
    if (!force && now < this.#publishedTime + UPDATE_THROTTLE_MS) {
      return;
    }

    const valuations = this.#sortValuations();
    if (!valuations) return;

    if (!force) this.#publishedTime = now;
    this.#subscribtion.publish({
      type: 'progress',
      valuations,
      req: this.#req!,
    });
  }

  #getCached(req: IValuationRequest): { data: IValuationLine[]; bestmove: string } | null {
    const cached = this.#cache[req.fen];
    if (!cached) return null;
    const { depth, movetime, multiPV } = cached.req;

    if (depth !== req.depth && (!depth || !req.depth || depth < req.depth)) {
      return null;
    }

    if (movetime !== req.movetime && (!movetime || !req.movetime || movetime < req.movetime)) {
      return null;
    }

    if (multiPV !== req.multiPV) return null;

    return cached;
  }

  // --- Message handlers

  #onMessage(e: MessageEvent<string>): void {
    if (this.isTerminated || typeof e.data !== 'string') {
      return;
    }

    const line = e.data.trim();

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[recieve]', line);
    }

    // Инициализация
    if (line === 'uciok') {
      this.#onMessage_Inited();
    }
    // Завершение обсчёта
    else if (line.startsWith('bestmove')) {
      this.#onMessage_Bestmove(line);
    }
    // Прогресс
    else if (line.startsWith('info') && line.match(/ score (cp|mate) /)) {
      this.#onMessage_Valuation(line);
    }
  }

  #onMessage_Inited() {
    if (this.specs.threads > 1) {
      this.#uci(`setoption name Threads value ${this.specs.threads}`);
    }
    this.#uci('ucinewgame');

    this.#uci(`setoption name UCI_Chess960 value true`);

    if (this.options.elo) {
      this.#uci(`setoption name UCI_LimitStrength value true`);
      this.#uci(`setoption name UCI_Elo value ${this.options.elo}`);
    } else {
      this.#uci(`setoption name UCI_LimitStrength value false`);
      this.#uci(`setoption name Skill Level value ${this.options.skill ?? 20}`);
    }

    this.#uci('isready');
    this.#status = 'idle';

    this.#subscribtion.publish({ type: 'initialized' });

    if (this.#reqNext) this.evaluate(this.#reqNext);
    return;
  }

  #onMessage_Valuation(line: string) {
    const valuation = this.#parseValuation(line);
    if (!valuation) return;
    this.#valuations[valuation.multiPV] = valuation;
    this.#publish();
  }

  #onMessage_Bestmove(line: string) {
    const stopping = this.isStopping;

    const bestmove = line.split(' ')[1]!;

    this.#status = stopping ? 'interrupted' : 'idle';
    const req = this.#req!;
    const valuations = this.#sortValuations();

    if (valuations) {
      this.#subscribtion.publish({
        type: stopping ? 'interrupted' : 'finished',
        valuations,
        req,
        bestmove,
      });

      // обновляем кэш полностью обсчитанными линиями
      if (!stopping) {
        this.#cache[req.fen] = { req, data: valuations, bestmove };
      }
    }

    if (this.#reqNext) this.evaluate(this.#reqNext);
  }

  // --- Parsing

  #parseValuation(line: string): IValuationLine | undefined {
    const valuation: IValuationLine = {
      fen: this.#req!.fen,
      depth: NaN,
      multiPV: 1,
      type: 'cp',
      valuation: NaN,
      moves: [],
      source: line,
    };

    let i = 0;
    let iteratingMoves = false;
    const tokens = line.split(' ');
    while (i < tokens.length) {
      const token = tokens[i]!;
      if (iteratingMoves) {
        valuation.moves.push(token);
      } else {
        switch (token) {
          case 'depth':
            valuation.depth = parseInt(tokens[++i]!, 10);
            break;
          case 'multipv':
            valuation.multiPV = parseInt(tokens[++i]!, 10);
            break;
          case 'cp':
          case 'mate':
            // игнорируем uppderbound и lowerbound
            if (tokens[i + 2] === 'upperbound' || tokens[i + 2] === 'lowerbound') {
              return;
            }

            if (tokens[i - 1] === 'score') {
              valuation.type = token;
              const score = parseFloat(tokens[++i]!);
              // превращаем относительную оценку в абсолютную: отрицательные
              // значения — всегда перевес чёрных, положительные — всегда белых
              valuation.valuation = score * (this.#req!.color === 'black' ? -1 : 1);
            }
            break;
          case 'pv':
            iteratingMoves = true;
            break;
        }
      }

      i++;
    }

    if (isNaN(valuation.depth) || isNaN(valuation.valuation)) {
      return;
    }

    return valuation;
  }

  // --- Utils

  /**
   * Инициализация нового движка
   */
  #initWorker(worker: Worker) {
    const controller = new AbortController();
    const { signal } = controller;

    worker.addEventListener('message', (e) => this.#onMessage(e), { signal });

    worker.addEventListener(
      'error',
      (e) => {
        if (this.isTerminated) return;

        console.error(e);

        if (this.#workerRetries >= MAX_WORKER_RETRIES) {
          console.error(
            `UciEngine: worker failed after ${MAX_WORKER_RETRIES} retries. Terminating engine.`,
          );
          this.terminate();
          return;
        }

        this.#workerRetries++;

        controller.abort();
        worker.terminate();

        this.#subscribtion.publish({ type: 'error', req: this.#req });

        this.#req = undefined;

        this.#worker = new Worker(this.specs.url);
        this.#initWorker(this.#worker);
      },
      { signal },
    );

    worker.addEventListener('messageerror', (e) => console.error(e), {
      signal,
    });

    // команда запуска движка
    this.#uci('uci');
  }

  /**
   * Сортировка линий
   */
  #sortValuations() {
    /*

    Сортировка разная для белых и чёрных. Самая выгодная линия для цвета должна
    быть выше, самая невыгодная ниже.

    white: 1. #+ по возрастанию 2. cp по убыванию 3. #- по возрастанию
    Пример: #5, #10, 10, 5, 0, -5, -10, #-10, #-5

    black: 1. -# по убыванию 2. cp по возрастанию 3. +# по убыванию
    Пример: #-5, #-10, -10, -5, 0 , 5, 10, #10, #5

    */

    if (!this.#req) {
      return;
    }

    const valuations = Object.values(this.#valuations);

    // Сортируем так, чтобы самая выгодная линия была первой
    const isWhite = this.#req.fen.match(' w ');
    const bmates: IValuationLine[] = [];
    const wmates: IValuationLine[] = [];
    const cps: IValuationLine[] = [];
    const empty: IValuationLine[] = [];
    for (let i = 0; i < valuations.length; i++) {
      const v = valuations[i]!;

      if (!v.moves.length) empty.push(v);
      else if (v.type === 'mate') {
        if (v.valuation < 0) bmates.push(v);
        else wmates.push(v);
      } else cps.push(v);
    }
    sortBy(cps, (v) => v.valuation);
    sortBy(bmates, (v) => v.valuation);
    sortBy(wmates, (v) => v.valuation);

    if (isWhite) {
      cps.reverse();
    } else {
      bmates.reverse();
      wmates.reverse();
    }
    return isWhite
      ? [...wmates, ...cps, ...bmates, ...empty]
      : [...bmates, ...cps, ...wmates, ...empty];
  }
}
